import { useState } from "react";
import { useSystemMessages } from "./hooks/useSystemMessages";
import { useUpdateSystemMessage } from "./hooks/useUpdateSystemMessage";
import { useDeleteSystemMessage } from "./hooks/useDeleteSystemMessage";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LoadingState } from "@/components/ui/loading-state";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Link as LinkIcon,
  Building2
} from "lucide-react";
import { format } from "date-fns";
import { SystemMessage, SystemMessageOrganization } from "@/models/system-message";
import { SystemMessageFormDialog } from "./components/SystemMessageFormDialog";
import {
  DISPLAY_TYPE_LABELS,
  STATUS_BADGE_VARIANT,
  STATUS_LABELS,
  TYPE_BADGE_VARIANT,
  TYPE_LABELS,
  displayText,
  getSystemMessageStatus
} from "./system-messages.meta";

const AudienceLabel = ({ organizations }: { organizations: SystemMessageOrganization[] }) => (
  <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
    <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>
      {organizations.length === 0
        ? "All organizations"
        : organizations.length <= 2
          ? organizations.map((org) => org.name).join(", ")
          : `${organizations.length} organizations`}
    </span>
  </div>
);

const SystemMessagesPage = () => {
  const { data: systemMessages, isLoading, error, refetch } = useSystemMessages();
  const updateSystemMessage = useUpdateSystemMessage();
  const deleteSystemMessage = useDeleteSystemMessage();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SystemMessage | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleCreate = () => {
    setEditing(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (message: SystemMessage) => {
    setEditing(message);
    setIsDialogOpen(true);
  };

  const handleToggleEnabled = (message: SystemMessage) => {
    updateSystemMessage.mutate({ id: message._id, payload: { enabled: !message.enabled } });
  };

  const handleDelete = (id: string) => {
    deleteSystemMessage.mutate(id, { onSuccess: () => setPendingDeleteId(null) });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="System Messages"
        description="Broadcast banners and popups to every organization, or just the ones you pick"
        action={
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New Message
          </Button>
        }
      />

      <SystemMessageFormDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setEditing(null);
        }}
        systemMessage={editing}
      />

      {isLoading ? (
        <LoadingState isLoading={isLoading} loadingText="Loading system messages..." />
      ) : error ? (
        <LoadingState
          error={error}
          errorTitle="Failed to load system messages"
          errorDescription="There was an error loading the system messages. Please try again."
          onRetry={() => refetch()}
        />
      ) : !systemMessages || systemMessages.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No system messages yet"
          description="Create one to broadcast a banner or popup to dashboard users."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Display</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {systemMessages.map((message) => {
                  const status = getSystemMessageStatus(message);
                  const isConfirmingDelete = pendingDeleteId === message._id;
                  return (
                    <TableRow key={message._id}>
                      <TableCell>
                        <Badge
                          variant={TYPE_BADGE_VARIANT[message.type]}
                          className="whitespace-nowrap"
                        >
                          {TYPE_LABELS[message.type]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[320px]">
                        {displayText(message.title) && (
                          <div dir="auto" className="font-medium text-foreground text-sm">
                            {displayText(message.title)}
                          </div>
                        )}
                        <p dir="auto" className="text-sm text-muted-foreground line-clamp-2">
                          {displayText(message.body)}
                        </p>
                        {message.linkUrl && (
                          <div className="flex items-center gap-1 text-xs text-primary mt-1">
                            <LinkIcon className="h-3 w-3" />
                            {displayText(message.linkText) || message.linkUrl}
                          </div>
                        )}
                        <div className="mt-1 flex gap-1">
                          {message.body.en?.trim() && (
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                              EN
                            </Badge>
                          )}
                          {message.body.ar?.trim() && (
                            <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                              AR
                            </Badge>
                          )}
                        </div>
                        {!message.dismissible && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            Not dismissible
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <AudienceLabel organizations={message.organizationIds ?? []} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {DISPLAY_TYPE_LABELS[message.displayType]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3" />
                            {message.startAt
                              ? format(new Date(message.startAt), "MMM d, yyyy HH:mm")
                              : "Immediately"}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3 w-3" />
                            {message.endAt
                              ? format(new Date(message.endAt), "MMM d, yyyy HH:mm")
                              : "No expiry"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[status]}>
                          {STATUS_LABELS[status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={message.enabled}
                          onCheckedChange={() => handleToggleEnabled(message)}
                          disabled={updateSystemMessage.isPending}
                          aria-label="Toggle enabled"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {isConfirmingDelete ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPendingDeleteId(null)}
                              disabled={deleteSystemMessage.isPending}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(message._id)}
                              disabled={deleteSystemMessage.isPending}
                            >
                              Confirm
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(message)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPendingDeleteId(message._id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SystemMessagesPage;
